"use client";

/**
 * useAuthenticatedWalletConnection — the canonical bridge between Privy authentication and wagmi signers.
 *
 * ## Why this hook exists
 *
 * Privy manages authentication (which wallet address signed in) while wagmi manages the live
 * connector (which wallet extension is currently active). These two can drift apart:
 *
 * - Mobile browsers kill the in-app browser session after phone sleep, disconnecting wagmi
 *   while Privy still considers the user authenticated.
 * - Users switch accounts in MetaMask, making wagmi's active address differ from the
 *   Privy-authenticated address.
 * - WalletConnect sessions expire or become stale without Privy knowing.
 *
 * This hook provides a single `ensureAuthenticatedWalletConnection()` function that:
 * 1. Validates the user is authenticated with Privy
 * 2. Finds the matching Privy wallet object
 * 3. Activates it as wagmi's active wallet (via `setActiveWallet`)
 * 4. Triggers a wagmi reconnect if needed
 * 5. Waits (up to 750ms) for wagmi's state to reflect the correct address
 * 6. Throws a typed `WalletConnectionError` if alignment fails
 *
 * ## How it fits in the architecture
 *
 * ```
 * Feature hooks (claim, swap, deposit, sign)
 *   └── useWalletActionGuard (prepare + execute + error normalization)
 *         └── useAuthenticatedWalletConnection  ← THIS HOOK
 *               ├── useAuthenticationProvider (Privy/TON/Local auth state)
 *               ├── useWallets (Privy wallet objects)
 *               └── useConfig / useReconnect / useAccount (wagmi state)
 * ```
 *
 * ## Exports
 *
 * - `WALLET_CONNECTION_ERROR_CODE` — typed const for all connection-level error codes
 * - `WalletConnectionError` — error class thrown by this hook
 * - `isWalletConnectionError()` / `isConnectorConnectionError()` — type guards
 * - `useAuthenticatedWalletConnection()` — the hook itself
 *
 * ## Return value
 *
 * - `authenticatedWalletAddress` — the canonical wallet address (from Privy auth, not wagmi)
 * - `authenticatedWalletConnectorType` — connector type (e.g. "wallet_connect", "injected")
 * - `ensureAuthenticatedWalletConnection()` — async fn that aligns wagmi or throws
 * - `getAuthenticatedWalletClient()` — gets a wagmi WalletClient for the authenticated address
 */
import { useCallback, useMemo, useRef } from "react";
import { normalizeAddress as coreNormalizeAddress } from "@cede/utils";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useWallets } from "@privy-io/react-auth";
import { useSetActiveWallet } from "@privy-io/wagmi";
import { getAddress } from "viem";
import { useAccount, useConfig, useReconnect } from "wagmi";
import { getWalletClient } from "wagmi/actions";

/** Typed error codes for wallet connection failures. Subset of WALLET_ACTION_ERROR_CODE. */
export const WALLET_CONNECTION_ERROR_CODE = {
  WALLET_NOT_AUTHENTICATED: "wallet-not-authenticated",
  WALLET_CONNECTION_INITIALIZING: "wallet-connection-initializing",
  WALLET_DISCONNECTED: "wallet-disconnected",
  WALLET_LOCKED: "wallet-locked",
  WALLET_MISMATCH: "wallet-mismatch",
  WALLET_CLIENT_UNAVAILABLE: "wallet-client-unavailable",
} as const;

export type WalletConnectionErrorCode =
  (typeof WALLET_CONNECTION_ERROR_CODE)[keyof typeof WALLET_CONNECTION_ERROR_CODE];

export class WalletConnectionError extends Error {
  code: WalletConnectionErrorCode;

  constructor(code: WalletConnectionErrorCode, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "WalletConnectionError";
    this.code = code;
  }
}

/** Null-safe wrapper around @cede/utils normalizeAddress (lowercases EVM, preserves Solana). */
const normalizeAddress = (value?: string | null) => (value ? coreNormalizeAddress(value) : undefined);

/** Reads wagmi's internal state synchronously to get the currently connected account. */
const getCurrentConnectedAccount = (config: ReturnType<typeof useConfig>) => {
  if (!config.state.current) return null;

  const connection = config.state.connections.get(config.state.current);
  const connectedAddress = connection?.accounts[0];

  return connectedAddress
    ? {
        address: getAddress(connectedAddress),
        chainId: connection?.chainId,
      }
    : null;
};

/**
 * Subscribes to wagmi config state and resolves when the expected address appears
 * as the active account, or rejects after timeoutMs. This handles the async gap
 * between calling setActiveWallet/reconnect and wagmi actually updating its state.
 */
const waitForAuthenticatedAccount = async ({
  config,
  expectedAddress,
  timeoutMs = 750,
}: {
  config: ReturnType<typeof useConfig>;
  expectedAddress: string;
  timeoutMs?: number;
}) => {
  const currentAccount = getCurrentConnectedAccount(config);
  if (normalizeAddress(currentAccount?.address) === expectedAddress) {
    return currentAccount;
  }

  return await new Promise<{ address: `0x${string}`; chainId?: number } | null>((resolve) => {
    let settled = false;

    const finish = (account: { address: `0x${string}`; chainId?: number } | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      unsubscribe();
      resolve(account);
    };

    const timeoutId = window.setTimeout(() => {
      const latestAccount = getCurrentConnectedAccount(config);

      if (normalizeAddress(latestAccount?.address) === expectedAddress) {
        finish(latestAccount);
        return;
      }

      finish(null);
    }, timeoutMs);

    const unsubscribe = config.subscribe(
      (state) => {
        if (!state.current) return null;

        const connection = state.connections.get(state.current);
        const connectedAddress = connection?.accounts[0];

        return connectedAddress
          ? {
              address: getAddress(connectedAddress),
              chainId: connection?.chainId,
            }
          : null;
      },
      (account) => {
        if (normalizeAddress(account?.address) !== expectedAddress) return;

        finish(account);
      },
    );
  });
};

export const isWalletConnectionError = (error: unknown): error is WalletConnectionError => {
  return error instanceof WalletConnectionError;
};

/**
 * Returns true for errors that indicate a transient connector issue (disconnected, initializing,
 * or client unavailable). These are retryable — the user's wallet may reconnect automatically.
 * Does NOT match wallet-mismatch or wallet-not-authenticated (those require user action).
 */
export const isConnectorConnectionError = (error: unknown): boolean => {
  if (isWalletConnectionError(error)) {
    return (
      error.code === WALLET_CONNECTION_ERROR_CODE.WALLET_CONNECTION_INITIALIZING ||
      error.code === WALLET_CONNECTION_ERROR_CODE.WALLET_DISCONNECTED ||
      error.code === WALLET_CONNECTION_ERROR_CODE.WALLET_CLIENT_UNAVAILABLE
    );
  }

  if (!(error instanceof Error)) return false;

  return error.name === "ConnectorNotConnectedError" || error.message.includes("Connector not connected");
};

/**
 * Ensures wagmi is aligned with the wallet the user authenticated with via Privy.
 *
 * Privy authentication and wagmi's active connector can drift apart on mobile browsers
 * or after provider reconnects. We validate the currently active wagmi address against
 * the authenticated Privy wallet before any write/sign operation.
 */
export const useAuthenticatedWalletConnection = () => {
  const config = useConfig();
  const { reconnectAsync } = useReconnect();
  const { address } = useAccount();
  const { wallets, ready: arePrivyWalletsReady } = useWallets();
  const { setActiveWallet } = useSetActiveWallet();
  const { authenticated, identityProvider, ready, user } = useAuthenticationProvider();
  const pendingConnectionRef = useRef<Promise<{ address: `0x${string}`; chainId?: number }> | null>(null);

  /**
   * The canonical wallet address for the authenticated user.
   * - Privy users: always the Privy-stored wallet address (not wagmi's active address)
   * - Non-Privy (TON/Local): wagmi address with fallback to auth provider wallet
   */
  const authenticatedWalletAddress = useMemo(() => {
    if (identityProvider === "privy") {
      return user?.wallet?.address ?? null;
    }

    return address ?? user?.wallet?.address ?? null;
  }, [address, identityProvider, user?.wallet?.address]);

  const authenticatedPrivyWallet = useMemo(() => {
    const normalizedAuthenticatedAddress = normalizeAddress(authenticatedWalletAddress);

    if (!normalizedAuthenticatedAddress) {
      return null;
    }

    return wallets.find((wallet) => normalizeAddress(wallet.address) === normalizedAuthenticatedAddress) ?? null;
  }, [authenticatedWalletAddress, wallets]);

  const doEnsureAuthenticatedWalletConnection = useCallback(async (): Promise<{
    address: `0x${string}`;
    chainId?: number;
  }> => {
    const fallbackMessage = "Your wallet appears to be disconnected. Please reconnect your wallet and try again.";
    let lastConnectionError: unknown = null;

    if (identityProvider !== "privy") {
      const connectedAccount = getCurrentConnectedAccount(config);
      const connectedAddress = connectedAccount?.address ?? (address ? getAddress(address) : null);

      if (!connectedAddress) {
        throw new WalletConnectionError(WALLET_CONNECTION_ERROR_CODE.WALLET_DISCONNECTED, fallbackMessage);
      }

      return {
        address: connectedAddress,
        chainId: connectedAccount?.chainId,
      };
    }

    if (!ready || !authenticated || !authenticatedWalletAddress) {
      throw new WalletConnectionError(
        WALLET_CONNECTION_ERROR_CODE.WALLET_NOT_AUTHENTICATED,
        "Please reconnect the wallet you used to sign in and try again.",
      );
    }

    const normalizedAuthenticatedAddress = normalizeAddress(authenticatedWalletAddress);

    if (!normalizedAuthenticatedAddress) {
      throw new WalletConnectionError(
        WALLET_CONNECTION_ERROR_CODE.WALLET_NOT_AUTHENTICATED,
        "Please reconnect the wallet you used to sign in and try again.",
      );
    }

    if (arePrivyWalletsReady && !authenticatedPrivyWallet) {
      throw new WalletConnectionError(
        WALLET_CONNECTION_ERROR_CODE.WALLET_DISCONNECTED,
        "Your authenticated wallet is not currently connected. Please reconnect the same wallet you used to sign in and try again.",
      );
    }

    const tryActivateAuthenticatedWallet = async () => {
      if (!authenticatedPrivyWallet) return;

      try {
        await setActiveWallet(authenticatedPrivyWallet);
      } catch (error) {
        lastConnectionError = error;
      }
    };

    const tryReconnectWallet = async () => {
      try {
        await reconnectAsync();
      } catch (error) {
        lastConnectionError = error;
      }
    };

    await tryActivateAuthenticatedWallet();
    await tryReconnectWallet();

    let connectedAccount =
      (await waitForAuthenticatedAccount({
        config,
        expectedAddress: normalizedAuthenticatedAddress,
      })) ?? getCurrentConnectedAccount(config);
    let activeAddress = normalizeAddress(connectedAccount?.address);

    if (activeAddress && activeAddress !== normalizedAuthenticatedAddress) {
      await tryActivateAuthenticatedWallet();
      await tryReconnectWallet();
      connectedAccount =
        (await waitForAuthenticatedAccount({
          config,
          expectedAddress: normalizedAuthenticatedAddress,
        })) ?? getCurrentConnectedAccount(config);
      activeAddress = normalizeAddress(connectedAccount?.address);
    }

    if (!activeAddress) {
      throw new WalletConnectionError(
        arePrivyWalletsReady
          ? WALLET_CONNECTION_ERROR_CODE.WALLET_DISCONNECTED
          : WALLET_CONNECTION_ERROR_CODE.WALLET_CONNECTION_INITIALIZING,
        arePrivyWalletsReady
          ? fallbackMessage
          : "Your wallet connection is still initializing. Please try again in a moment.",
        lastConnectionError ? { cause: lastConnectionError } : undefined,
      );
    }

    if (activeAddress !== normalizedAuthenticatedAddress) {
      throw new WalletConnectionError(
        WALLET_CONNECTION_ERROR_CODE.WALLET_MISMATCH,
        "The connected wallet does not match the wallet you used to sign in. Please reconnect the same wallet and try again.",
        lastConnectionError ? { cause: lastConnectionError } : undefined,
      );
    }

    return {
      address: getAddress(authenticatedWalletAddress),
      chainId: connectedAccount?.chainId,
    };
  }, [
    address,
    arePrivyWalletsReady,
    authenticated,
    authenticatedPrivyWallet,
    authenticatedWalletAddress,
    config,
    identityProvider,
    ready,
    reconnectAsync,
    setActiveWallet,
  ]);

  /**
   * Deduplicating wrapper — if a connection attempt is already in-flight,
   * returns the same promise instead of starting a second one. Prevents
   * race conditions when multiple UI actions fire simultaneously.
   */
  const ensureAuthenticatedWalletConnection = useCallback(() => {
    if (pendingConnectionRef.current) {
      return pendingConnectionRef.current;
    }

    const pendingConnection = doEnsureAuthenticatedWalletConnection().finally(() => {
      pendingConnectionRef.current = null;
    });

    pendingConnectionRef.current = pendingConnection;

    return pendingConnection;
  }, [doEnsureAuthenticatedWalletConnection]);

  /**
   * Gets a wagmi WalletClient for the authenticated address. Ensures the connection
   * first, then tries to get the client. If it fails, reconnects and retries once
   * before throwing WALLET_CLIENT_UNAVAILABLE.
   */
  const getAuthenticatedWalletClient = useCallback(async () => {
    const { address } = await ensureAuthenticatedWalletConnection();

    try {
      return await getWalletClient(config, { account: address });
    } catch (error) {
      let reconnectError: unknown = null;

      try {
        await reconnectAsync();
      } catch (retryError) {
        reconnectError = retryError;
      }

      try {
        return await getWalletClient(config, { account: address });
      } catch (clientError) {
        throw new WalletConnectionError(
          WALLET_CONNECTION_ERROR_CODE.WALLET_CLIENT_UNAVAILABLE,
          "Your wallet is connected, but the signing client is unavailable. Please reconnect your wallet and try again.",
          { cause: reconnectError ?? clientError ?? error },
        );
      }
    }
  }, [config, ensureAuthenticatedWalletConnection, reconnectAsync]);

  return {
    authenticatedWalletAddress,
    authenticatedWalletConnectorType: authenticatedPrivyWallet?.connectorType ?? null,
    ensureAuthenticatedWalletConnection,
    getAuthenticatedWalletClient,
  };
};
